import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "@reach/router";
import { Offcanvas, Dropdown } from "bootstrap";
import { ReactComponent as IconPlusCircleFill } from 'bootstrap-icons/icons/plus-circle-fill.svg'
import { ReactComponent as IconLayoutSidebarInset } from 'bootstrap-icons/icons/layout-sidebar-inset.svg'
import { ReactComponent as IconLayoutSidebar } from 'bootstrap-icons/icons/layout-sidebar.svg'
import { ReactComponent as IconJournalText } from 'bootstrap-icons/icons/journal-text.svg'
import { ReactComponent as IconGear } from 'bootstrap-icons/icons/gear.svg'
// Puerta con la flecha saliendo, como en el handoff. `power` era el símbolo de apagado,
// que en el menú se lee como "apagar el sistema" y no como "salir de la cuenta".
import { ReactComponent as IconBoxArrowRight } from 'bootstrap-icons/icons/box-arrow-right.svg'
import { ReactComponent as IconArrowLeftRight } from 'bootstrap-icons/icons/arrow-left-right.svg'
import { ReactComponent as IconArrowRepeat } from 'bootstrap-icons/icons/arrow-repeat.svg'
import { ReactComponent as IconInfoCircle } from 'bootstrap-icons/icons/info-circle.svg'
import { ReactComponent as IconShop } from 'bootstrap-icons/icons/shop.svg'
import { ReactComponent as IconGem } from 'bootstrap-icons/icons/gem.svg'

import Crontu from "Components/Crontu/Crontu";
import DrApp, { DRAPP_COLOR } from "Components/DrApp/DrApp";
import useUser from "hooks/useUser";
import useQuery from "hooks/useQuery";
import useCrontuLinks from "hooks/useCrontuLinks";
import useDownload from "hooks/useDownload";
import useTeamID from "hooks/useTeamID";
import useBetaFeatures from "hooks/useBetaFeatures";
import useWebUiToggle from "hooks/useWebUiToggle";
import { effectiveWebUi } from "lib/uiPreference";
import useStorePrescription from "App/Prescription/useStore";
import useStoreGlobal from "../useStore";

import styles from "./styles.module.scss";
import useStoreRooms from "../Room/useStore";
import useStoreEvents from "../Events/useStore";
import EventForm from "../Events/Form/Form";
import NewRemoteCall from "./NewRemoteCall";
import { usePlanLock, LockedLabel } from "Components/FeatureBloqueada/PlanLock";
import useTerms from "hooks/useTerms";
import TermsAcceptModal from "Components/TermsBanner/TermsAcceptModal";

const { REACT_APP_MARKETPLACE_ROOT_URL, REACT_APP_BULK_MESSAGES_ALLOWED_TEAM_IDS, REACT_APP_APPSTORE_URL } = process.env;
const bulkMessagesAllowedTeamIDs = (REACT_APP_BULK_MESSAGES_ALLOWED_TEAM_IDS || "").split(",").filter(Boolean);

const urlPath = (url) => {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

// Mismo formato de mini-card que el listado de profesionales (App/Setup/Resources/Home).
// `Item` es el preset de la marca (Components/Crontu, Components/DrApp): la lista no
// sabe de logos ni colores.
// `color` tiñe el rótulo de la lista; sin él queda el gris de siempre. `text-muted` se
// saca cuando hay color porque Bootstrap lo declara con `!important` y le ganaría al
// estilo inline.
const DirectoryList = ({ title, items, Item, color, children }) => {
  if (!items.length && !children) return null;
  return (
    <>
      <div
        className={`form-label small mb-1 ${color ? "" : "text-muted"}`}
        style={color ? { color } : undefined}
      >
        {title}
      </div>
      {children}
      {!!items.length && (
        <ul className="list-unstyled mb-2 overflow-auto" style={{ maxHeight: "88px" }}>
          {items.map(({ key, path, url }) => (
            <li key={`navbar-directory-${title}-${key}`} className="mb-1">
              <Item link={url} label={path} size="14" />
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default function () {
  const $offcanvas = useRef(null)
  const $offcanvasNavbar = useRef(null)
  const $accountToggle = useRef(null)
  const today = new Date();
  const storeGlobal = useStoreGlobal()
  const { t, i18n } = useTranslation();
  const user = useUser();
  const location = useLocation();
  const openRoom = useStoreRooms((state) => state.open);
  const date = useStoreEvents(state => state.date)
  const [event, setEvent] = useState();
  const [newRemoteCall, setNewRemoteCall] = useState(false);
  // Punto de entrada persistente a los T&C: si el usuario postergó el banner
  // ("Más tarde"), igual necesita poder abrirlos y aceptarlos desde acá. Se
  // muestra mientras haya aceptación pendiente (needsAcceptance, reactivo vía
  // react-query) y desaparece al aceptar.
  const terms = useTerms();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const downloadEvents = useDownload("events");
  const downloadConsumers = useDownload("consumers");
  // Ambas descargas de Reportes pegan a rutas que el back corta con
  // reports.bulk_export (events/download, consumers/download).
  const exportLock = usePlanLock("reports.bulk_export");
  const telemedicineLock = usePlanLock("events.telemedicine");
  // AppStore (QA card 854): sin `apps.access` el ítem sigue en el menú y abre el modal de
  // plan, con "Ir al AppStore" para ver las apps igual.
  const appsLock = usePlanLock("apps.access", {
    secondaryAction: REACT_APP_APPSTORE_URL && { label: t("plan_locked_apps_go_to_store", "Ir al AppStore"), href: REACT_APP_APPSTORE_URL },
  });
  const currentTeamID = useTeamID();
  const team = useQuery(currentTeamID);
  const prescription = useStorePrescription();
  const webUiFeatures = useBetaFeatures();
  const webUiToggle = useWebUiToggle(currentTeamID);
  const [range, setRange] = useState({
    startsAt: new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    ).toLocaleDateString("fr-ca"),
    endsAt: new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    ).toLocaleDateString("fr-ca"),
  });

  const [accountOpened, setAccountOpened] = useState(false);

  const resources = useQuery(window._l("/resources").substr(1), {
    initialData: [],
    enabled: accountOpened,
  });

  // Se llama acá y no junto al resto de la lógica de Crontu porque más abajo hay un
  // return temprano y los hooks no pueden quedar detrás de él.
  const crontuByResource = useCrontuLinks(resources.data || []);

  const teamLabel = (team) =>
    team.label
      ? `${team.label} (${team.id.replace("teams/", "")})`
      : team.id.replace("teams/", "");

  // El menú de cuenta solo se cierra al tocar fuera (data-bs-auto-close="outside"),
  // así que las acciones que navegan lo cierran a mano.
  const closeAccount = () => {
    try {
      Dropdown.getInstance($accountToggle.current)?.hide();
    } catch { }
  };

  // Bug real (auditoría 2026-08-21): data-bs-dismiss="offcanvas" en un <a>
  // (los Link de @reach/router se renderean como <a>) hace que el listener
  // global de Bootstrap (enableDismissTrigger, delegado en document) llame
  // event.preventDefault() ANTES de que el propio Link llegue a chequear
  // shouldNavigate (!event.defaultPrevented) — Link ve defaultPrevented ya
  // en true y NUNCA navega. El offcanvas se cerraba igual (por eso el fix
  // anterior de esta misma auditoría "se veía" andar) pero el click en
  // "Pacientes"/"Agenda"/etc dejaba de llevar a ningún lado. Se cierra a
  // mano, mismo patrón que closeAccount de arriba.
  const closeOffcanvasNavbar = () => {
    try {
      Offcanvas.getOrCreateInstance($offcanvasNavbar.current)?.hide();
    } catch { }
  };

  const selectTeam = ({ target: { value } }) => {
    if (value === "new") {
      window.location.href = "/teams/new";
    } else {
      const newPath = location.pathname.replace(currentTeamID, value);
      if (/\/setup\/resources\/(?!new(?:\/|$))[^/]+/.test(newPath)) {
        window.location.href = newPath.replace(/\/setup\/resources\/.+/, '/setup/resources');
      } else {
        window.location.href = newPath;
      }
    }
  };

  useEffect(() => {
    const handler = () => setEvent();
    window.document.addEventListener("hidden.bs.offcanvas", handler);
    return () => window.document.removeEventListener("hidden.bs.offcanvas", handler);
  }, []);

  if (!user.state.teams) return null;
  const slug = team.data?.slug || currentTeamID.replace("teams/", "");
  const marketplaceUrl = `${REACT_APP_MARKETPLACE_ROOT_URL.replace(
    "://",
    `://${slug}.`
  )}`;

  // La cartilla del equipo es el subdominio a secas; el tramo `/equipos/<teamID>` que
  // llevaba antes es redundante — el slug ya identifica al equipo. Es la misma URL que
  // muestra App/Setup/Teams.
  const teamMarketplaceUrl = marketplaceUrl;

  // Cartilla de DrApp — solo la del equipo. Las de cada profesional se ven en su ficha.
  const drappLinks = [
    { key: "team", path: `/${slug}`, url: teamMarketplaceUrl },
  ];

  // --- Crontu ---
  // La cartilla de profesional que le corresponde al usuario es una sola: la del
  // profesional que tiene asignado, o la del único del equipo si no tiene ficha. Nunca
  // se listan las de los colegas. El endpoint ya excluye eliminados.
  const crontuResources = resources.data || [];
  const crontuResource =
    crontuResources.find((resource) => resource.id === user.team?.resourceID) ||
    (crontuResources.length === 1 ? crontuResources[0] : null);

  // Qué corresponde en la parte del profesional:
  //   link      → su cartilla
  //   setup     → banner para configurar el Crontu de ese profesional
  //   resources → banner al listado: hay varios profesionales y al menos uno publicado
  //   ""        → nada; sin ficha propia y sin ninguno publicado el usuario no tiene
  //               injerencia sobre esa configuración
  // El link sale del índice de Crontu (localStorage), no del resource.
  const crontuUrlOf = (resource) => crontuByResource[resource?.id]?.url || "";

  const crontuMode = crontuUrlOf(crontuResource)
    ? "link"
    : crontuResource
      ? "setup"
      : crontuResources.some((resource) => !!crontuUrlOf(resource))
        ? "resources"
        : "";

  // El link de la clínica se disponibiliza siempre que esté configurado, por debajo del
  // link del profesional.
  const crontuLinks = [
    crontuMode === "link" && {
      key: crontuResource.id,
      url: crontuUrlOf(crontuResource),
    },
    team.data?.externalMarketplaceUrl && { key: "team", url: team.data.externalMarketplaceUrl },
  ]
    .filter(Boolean)
    .map(({ key, url }) => ({ key, url, path: urlPath(url) }));

  const crontuResourcesPath = window._l("/setup/resources");
  const crontuSetupPath = crontuResource
    ? window._l(`/setup/resources/${crontuResource.id.replace("resources/", "")}/crontu`)
    : crontuResourcesPath;

  // const enableSidebarBtn = /\/events\/calendar\/|\/events\/agenda\/|\/consumers\/[a-zA-Z0-9]+\/ehr\//i.test(location.pathname)
  const enableSidebarBtn = /\/events\/calendar\/|\/events\/agenda\//i.test(location.pathname)

  return (
    <>
      <nav className="sticky-top navbar navbar-expand-lg navbar-light bg-white border-bottom d-print-none">
        <div className="container-fluid">
          <div className="dropdown">
            <button className="btn text-primary" data-bs-toggle="dropdown" aria-label="Nuevo">
              <IconPlusCircleFill width="25" height="25" />
            </button>
            <div className={`${styles["plus-container"]} dropdown-menu p-0 shadow-lg border-0 m-2 `}>
              <button
                className="btn btn-link link-dark btn-sm p-3 w-100 text-start border-bottom text-truncate"
                type="button" data-bs-toggle="offcanvas" data-bs-target="#offcanvasEvent"
                onClick={() => setEvent({})}
              >
                {t("new", { label: t("event") })}
              </button>

              <button
                className="btn btn-link link-dark btn-sm p-3 w-100 text-start border-bottom text-truncate"
                data-testid="navbar-new-remote-call"
                onClick={telemedicineLock.guard(() => setNewRemoteCall(true))}
              >
                <LockedLabel blocked={telemedicineLock.blocked}>{t("new", { label: t("remote_link"), context: "female" })}</LockedLabel>
              </button>

              <button
                className="btn btn-link link-dark btn-sm p-3 w-100 text-start border-bottom text-truncate"
                data-bs-toggle="modal"
                data-bs-target="#consumersModal"
              >
                {t("new", { label: t("consumer") })}
              </button>

              {bulkMessagesAllowedTeamIDs.includes(currentTeamID) && (
                <button
                  className="btn btn-link link-dark btn-sm p-3 w-100 text-start text-truncate"
                  data-bs-toggle="modal"
                  data-bs-target="#messageModal"
                >
                  {t("new", { label: t("message") })}
                </button>
              )}

              {(user.state.isStaff ||
                (user.actions.has("ehr") &&
                  user.state.country === "ar" &&
                  user.team?.resourceID)) && (
                  <button
                    className="btn btn-link link-dark btn-sm p-3 w-100 text-start border-top text-truncate"
                    onClick={prescription.toggle}
                  >
                    {t("new", { label: t("prescription") })}
                  </button>
                )}
            </div>
          </div>
          <button
            disabled={!enableSidebarBtn}
            className={`btn ${!enableSidebarBtn ? 'opacity-25' : ''}`}
            aria-label="Toggle sidebar"
            onClick={() => storeGlobal.update({ sidebar: !storeGlobal.sidebar })}
          >
            <IconLayoutSidebarInset width="25" height="25" className={storeGlobal.sidebar ? 'text-dark' : 'text-secondary opacity-75'} />
          </button>
          <button className="navbar-toggler border-0 text-dark p-0" type="button" data-bs-toggle="offcanvas" data-bs-target="#offcanvasNavbar" >
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div ref={$offcanvasNavbar} className="offcanvas offcanvas-end" id="offcanvasNavbar">
            <div className="offcanvas-header">
              <h5 className="offcanvas-title" id="offcanvasNavbarLabel">DrApp</h5>
              <button type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
            </div>
            <div className="offcanvas-body align-items-center">
              <ul className="navbar-nav me-auto gap-lg-3">
                {/* Bug real (auditoría 2026-08-18, UX; corregido 2026-08-21
                    de la forma correcta): estos links de navegación
                    necesitan cerrar el offcanvas al navegar en mobile — en
                    mobile, tocar "Agenda"/"Pacientes"/etc navegaba a la
                    sección pero el drawer se quedaba abierto tapando el
                    contenido. data-bs-dismiss="offcanvas" en un <a> rompe la
                    navegación de @reach/router (ver closeOffcanvasNavbar
                    más arriba) — se usa onClick en su lugar. */}
                <li className="nav-item">
                  <Link
                    to={window._l(`/events/calendar/${date || today.toLocaleDateString("fr-ca")}`)}
                    className={`nav-link ${location.pathname.includes("calendar") ? "active" : ""}`}
                    onClick={closeOffcanvasNavbar}
                  >
                    {t("calendar", "Calendario")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to={window._l(`/events/agenda/${date || today.toLocaleDateString("fr-ca")}`)}
                    className={`nav-link ${location.pathname.includes("agenda") ? "active" : ""}`}
                    onClick={closeOffcanvasNavbar}
                  >
                    {t("agenda")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to={window._l("/consumers")}
                    className={`nav-link ${location.pathname.includes("consumers") ? "active" : ""}`}
                    onClick={closeOffcanvasNavbar}
                  >
                    {t("consumer_plural")}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to={window._l("/setup/resources")}
                    className={`nav-link ${location.pathname.includes("resources") ? "active" : ""}`}
                    onClick={closeOffcanvasNavbar}
                  >
                    {t("resource_plural")}
                  </Link>
                </li>
                {user.actions.has('agreements', true) && (
                  <li className="nav-item dropdown">
                    <a className="nav-link dropdown-toggle" href="#" id="navbarDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                      {t("report_plural", "Reportes")}
                    </a>
                    {/* Fix responsive (2026-08-14): tope de 320px fijo,
                        combinado con las dos fechas de abajo sin flex-wrap,
                        forzaba un ancho mínimo mayor al viewport en un
                        celular chico. min() cubre lo mismo en desktop. */}
                    <div className="dropdown-menu p-0" style={{ maxWidth: "min(320px, calc(100vw - 24px))" }}>
                      <button
                        data-testid="navbar-report-download-consumers"
                        onClick={exportLock.guard(downloadConsumers.start)}
                        disabled={downloadConsumers.isLoading}
                        className="btn border-bottom btn-link d-block w-100 text-start px-3 py-2"
                      >
                        {downloadConsumers.isLoading && (
                          <div
                            className="spinner-border spinner-border-sm text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">{t("loading")}</span>
                          </div>
                        )}{" "}
                        <LockedLabel blocked={exportLock.blocked}>{t("consumer_plural")}</LockedLabel>
                      </button>
                      <div>
                        <h6 className="dropdown-header">{t("event_plural")}</h6>
                        <div className="px-3">
                          {/* Fix responsive (2026-08-14): las 2 fechas
                              (136px c/u) sin flex-wrap forzaban ~300px de
                              ancho mínimo pase lo que pase — con wrap, si no
                              entran en una fila pasan a apilarse. */}
                          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                            <div
                              data-tooltip-id="app-tooltip"
                              data-tooltip-content={t(
                                "event_report_startsAt_tip",
                                "Ingrese la fecha desde cuando quiere incluir turnos. Por defecto es principio de mes"
                              )}
                              style={{ maxWidth: "136px" }}
                            >
                              <label>{t("events_report_startsAt", "Desde")}</label>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={range.startsAt || ""}
                                onChange={(e) =>
                                  setRange({ ...range, startsAt: e.target.value })
                                }
                              />
                            </div>
                            <div
                              data-tooltip-id="app-tooltip"
                              data-tooltip-content={t(
                                "event_report_endsAt_tip",
                                "Ingrese la fecha hasta cuando quiere incluir turnos. Por defecto es fin de mes"
                              )}
                              style={{ maxWidth: "136px" }}
                            >
                              <label>{t("events_report_endsAt", "Hasta")}</label>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={range.endsAt || ""}
                                onChange={(e) =>
                                  setRange({ ...range, endsAt: e.target.value })
                                }
                              />
                            </div>
                          </div>
                          <button
                            data-testid="navbar-report-download-events"
                            onClick={exportLock.guard(() => downloadEvents.start(range))}
                            disabled={downloadEvents.isLoading}
                            className="btn btn-link px-0"
                          >
                            {downloadEvents.isLoading && (
                              <div
                                className="spinner-border spinner-border-sm text-primary"
                                role="status"
                              >
                                <span className="visually-hidden">{t("loading")}</span>
                              </div>
                            )}{" "}
                            <LockedLabel blocked={exportLock.blocked}>{t("download_events_reports", "Descargar")}</LockedLabel>
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                )}
              </ul>

              <div className="dropdown">
                <button
                  ref={$accountToggle}
                  className="btn w-100 w-lg-auto px-0 px-lg-3 text-start d-flex gap-2"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  onClick={() => setAccountOpened(true)}
                >
                  <div className="d-none d-lg-block">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="32"
                      width="32"
                      className="text-secondary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <div className="small">{user.state.email}</div>
                    <div className="text-muted small lh-1">
                      {team?.data?.label || currentTeamID.replace("teams/", "")}
                    </div>
                  </div>
                </button>
                <div className={`${styles["account-container"]} dropdown-menu-lg-end dropdown-menu p-0 shadow-lg border-0 mt-2 `}>
                  <div>
                    <div className="d-flex flex-column p-3 pb-2">
                      <label htmlFor="teamSwitcher" className="form-label small text-muted mb-1">
                        {t("account", "Cuenta")}
                      </label>
                      <select
                        id="teamSwitcher"
                        className="form-select form-select-sm bg-light border-0"
                        onChange={selectTeam}
                        value={currentTeamID}
                      >
                        {/* El equipo actual puede no estar en la lista del usuario
                            (staff entrando a un equipo ajeno): sin esta opción el
                            select no tendría con qué representar su propio valor. */}
                        {!user.state.teams.find((team) => team.id === currentTeamID) && (
                          <option value={currentTeamID}>
                            {currentTeamID.replace("teams/", "")}
                          </option>
                        )}
                        {user.state.teams.map((team) => (
                          <option key={`sidebar-team-${team.id}`} value={team.id}>
                            {teamLabel(team)}
                          </option>
                        ))}
                        <option disabled>------------------</option>
                        <option value="new">{t("create_new_team")}</option>
                      </select>
                    </div>
                    {/* Deshabilitado a propósito (auditoría 2026-08-16) — confirmar con Nacho si hay que reactivarlo o eliminarlo */}
                    {!!team.data?.subscription?.status && false && (
                      <a
                        href="https://www.mercadopago.com.ar/subscriptions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-link p-3 w-100 text-start"
                      >
                        {t("subscriptions", "Suscripciones")}
                      </a>
                    )}
                    <div className={`bg-light border-top px-3 py-2 ${styles["crontu-section"]}`}>
                      <DirectoryList title="Crontu" items={crontuLinks} Item={Crontu}>
                        {(crontuMode === "setup" || crontuMode === "resources") && (
                          // Sin borde: la card se distingue por el blanco sobre el gris de
                          // la sección, no por un marco de color.
                          <div className="mb-2 rounded bg-white p-2 text-center">
                            <div className="small fw-bold mb-2">
                              {crontuMode === "setup"
                                ? t("crontu_promo_title", "¿Queres recibir pacientes nuevos online?")
                                : t("crontu_resources_title", "Tenés profesionales en Crontu")}
                            </div>
                            <Crontu
                              to={crontuMode === "setup" ? crontuSetupPath : crontuResourcesPath}
                              label={
                                crontuMode === "resources"
                                  ? t("crontu_view_resources", "Ver Profesionales")
                                  : undefined
                              }
                              onClick={closeAccount}
                              size="14"
                              className="justify-content-center"
                            />
                          </div>
                        )}
                      </DirectoryList>
                      {!!drappLinks.length && (
                        // `mx-n3 px-3` estira la línea de punta a punta: el contenedor de
                        // la sección tiene `px-3`, así que sin los márgenes negativos el
                        // separador quedaría corto de los dos lados. El padding se
                        // devuelve adentro para que el contenido no se corra.
                        <div className={`border-top pt-2 mx-n3 px-3 ${styles["drapp-section"]}`}>
                          <DirectoryList title="DrApp" items={drappLinks} Item={DrApp} color={DRAPP_COLOR} />
                        </div>
                      )}
                    </div>
                    <div className="d-flex flex-column bg-white border-top">
                      {/* Bug real (auditoría 2026-08-18, UX; corregido
                          2026-08-21 de la forma correcta): mismo problema que
                          los links principales del menú, más un segundo
                          nivel acá — este dropdown usa
                          data-bs-auto-close="outside" (necesario para el
                          selector de equipo/DirectoryList de acá arriba), que
                          deja explícitamente sin cerrar cualquier click
                          ADENTRO del menú, así que hace falta cerrar ambos
                          (dropdown de cuenta + offcanvas) a mano.
                          data-bs-dismiss="offcanvas" en un <a> rompe la
                          navegación de @reach/router (ver
                          closeOffcanvasNavbar más arriba) — se cierran los
                          dos con onClick, no con el atributo. */}
                      {user.state.isStaff && (
                        <>
                          <Link to={window._l('/monitor')} className={`p-3 btn btn-link w-100 d-flex align-items-center justify-content-center gap-2 ${styles["account-action"]}`} onClick={() => { closeAccount(); closeOffcanvasNavbar(); }}>
                            <IconJournalText width="16" height="16" className="flex-shrink-0" />
                            {t('monitor_mode', 'Monitor')}
                          </Link>
                          <Link
                            to={window._l('/migrate-to-team')}
                            className={`p-3 btn btn-link w-100 d-flex align-items-center justify-content-center gap-2 ${styles["account-action"]}`}
                            onClick={() => { closeAccount(); closeOffcanvasNavbar(); }}
                          >
                            <IconArrowLeftRight width="16" height="16" className="flex-shrink-0" />
                            {t('migrate_consumers_to_team', 'Migrar pacientes')}
                          </Link>
                        </>
                      )}
                      <Link
                        to={window._l("/setup/account")}
                        className={`p-3 btn btn-link w-100 d-flex align-items-center justify-content-center gap-2 ${styles["account-action"]}`}
                        onClick={() => { closeAccount(); closeOffcanvasNavbar(); }}
                      >
                        <IconGear width="16" height="16" className="flex-shrink-0" />
                        {t("configuration", "Configuracion")}
                      </Link>
                      {/* Planes (card 1053): la ve cualquier usuario, no sólo admins. */}
                      <Link
                        to={window._l("/planes")}
                        data-testid="navbar-planes"
                        className={`p-3 btn btn-link w-100 d-flex align-items-center justify-content-center gap-2 ${styles["account-action"]}`}
                        onClick={() => { closeAccount(); closeOffcanvasNavbar(); }}
                      >
                        <IconGem width="16" height="16" className="flex-shrink-0" />
                        {t("planes_menu", "Planes")}
                      </Link>
                      {REACT_APP_APPSTORE_URL && (
                        <a
                          href={REACT_APP_APPSTORE_URL}
                          data-testid="navbar-appstore"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`p-3 btn btn-link w-100 d-flex align-items-center justify-content-center gap-2 ${styles["account-action"]}`}
                          onClick={(event) => { closeAccount(); closeOffcanvasNavbar(); appsLock.guard()(event); }}
                        >
                          <IconShop width="16" height="16" className="flex-shrink-0" />
                          <LockedLabel blocked={appsLock.blocked}>{t("appstore_cta", "App Store")}</LockedLabel>
                        </a>
                      )}
                      {webUiFeatures.webUiAvailable && (
                        // Feedback real (@nachoconstenla, 2026-08-26): sin esto,
                        // optar/rechazar la nueva UI (NewUiBanner) era una
                        // decisión de un solo sentido — ni el que la probó y
                        // volvió, ni el que la rechazó al principio, tenían
                        // forma de cambiar de opinión después.
                        //
                        // Bug real (QA-Cormos, 2026-09-02): acá se usaba
                        // `webUiFeatures.webUi` (el flag REAL) tanto para el
                        // texto como para lo que se manda al toggle — un
                        // usuario que vuelve de `web` con `?ui=legacy` sigue
                        // teniendo `webUi:true` real (el flag no se apaga solo
                        // por navegar a legacy), así que el botón mostraba
                        // "Volver a la versión anterior" (equivocado, ESO es
                        // lo que falta) y al clickear mandaba `enabled:false`
                        // — apagaba el flag real en vez de llevar a la nueva
                        // UI, y como useWebUiToggle sólo redirige cuando
                        // `enabled` es true, tampoco pasaba nada visible.
                        // effectiveWebUi mira el override de sesión primero.
                        <button
                          className={`p-3 btn btn-link w-100 d-flex align-items-center justify-content-center gap-2 ${styles["account-action"]}`}
                          disabled={webUiToggle.isLoading}
                          onClick={() => {
                            webUiToggle.setWebUi(!effectiveWebUi(webUiFeatures.webUi));
                            closeAccount();
                            closeOffcanvasNavbar();
                          }}
                        >
                          <IconArrowRepeat width="16" height="16" className="flex-shrink-0" />
                          {effectiveWebUi(webUiFeatures.webUi)
                            ? t("switch_to_legacy_ui", "Volver a la versión anterior")
                            : t("switch_to_new_ui", "Probar la nueva interfaz")}
                        </button>
                      )}
                      {terms.showNavCta && (
                        <button
                          className={`p-3 btn btn-link w-100 d-flex align-items-center justify-content-center gap-2 ${styles["account-action"]}`}
                          data-testid="navbar-terms-cta"
                          onClick={() => {
                            closeAccount();
                            setShowTermsModal(true);
                          }}
                        >
                          <IconInfoCircle width="16" height="16" className="flex-shrink-0" />
                          {t("terms_pending_cta", "Términos de IA pendientes")}
                        </button>
                      )}
                      <button
                        className={`p-3 btn btn-link w-100 d-flex align-items-center justify-content-center gap-2 ${styles["account-action-danger"]}`}
                        onClick={user.actions.logout}
                      >
                        <IconBoxArrowRight width="16" height="16" className="flex-shrink-0" />
                        {t("logout")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
      {/* Bug real (auditoría 2026-08-25, reportado por Juan Cruz QA): el
          ancho por defecto de Bootstrap (400px) le deja a la fila de
          fecha/hora del formulario (Events/Form/Form.js) ~367px de
          contenido real — matemáticamente exacto al ancho que ambos
          <input> nativos necesitan sin envolver (medido: 221px fecha +
          138px hora + 8px gap = 367px), cero margen. Cualquier variación
          mínima de fuente/locale del navegador la hacía pasar a dos líneas
          incluso en desktop. Se agranda el offcanvas para dar margen real. */}
      <div ref={$offcanvas} className="offcanvas offcanvas-start" tabIndex="-1" id="offcanvasEvent" aria-labelledby="offcanvasEventForm" style={{ '--bs-offcanvas-width': '460px' }}>
        <div className="offcanvas-body">
          {event && (
            <EventForm
              time={false}
              day={false}
              reschedule
              onClose={() => {
                try {
                  Offcanvas.getOrCreateInstance($offcanvas.current).hide()
                } catch { }
              }}
            />
          )}
        </div>
      </div>

      <NewRemoteCall
        show={newRemoteCall}
        onClose={() => setNewRemoteCall(false)}
        openRoom={openRoom}
      />
      {exportLock.modal}
      {telemedicineLock.modal}
      {appsLock.modal}

      <TermsAcceptModal show={showTermsModal} onHide={() => setShowTermsModal(false)} />
    </>
  )
}
